import { QuestionAnswer } from "./-components/question-answer"
import { useState, useEffect } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { api } from "@convex/_generated/api"
import { formatDistanceToNow, parseISO } from "date-fns"
import { formatInLocalTimezone } from "@/lib/timezone-utils"
import { Calendar, Clock, CheckCircle } from "lucide-react"
import type { Id } from "@convex/_generated/dataModel"

export const Route = createFileRoute(
  "/(app)/app/quizzes/$quizStateId/instance"
)({
  component: RouteComponent,
})

function RouteComponent() {
  const { quizStateId } = Route.useParams()
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isAutoSubmitting, setIsAutoSubmitting] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)

  const markRoundAnswers = useMutation(api.quizState.handlers.markRoundAnswers)
  const submitQuizForReview = useMutation(
    api.quizState.handlers.submitQuizForReview
  )
  const expireRoundTimer = useMutation(api.quizState.handlers.expireRoundTimer)

  // Fetch quiz state and quiz details
  const quizStateData = useQuery(api.quizState.handlers.getQuizStateById, {
    quizStateId: quizStateId as Id<"quizState">,
  })

  // Fetch questions (always round 1 for individual quizzes)
  const questions = useQuery(
    api.quizzesToQuestions.handlers.getQuestionsByQuizStateAndRound,
    {
      quizStateId: quizStateId as Id<"quizState">,
      roundNumber: 1,
    }
  )

  const isRoundComplete = useQuery(api.quizState.handlers.isRoundComplete, {
    quizStateId: quizStateId as Id<"quizState">,
  })

  const quizState = quizStateData?.quizState
  const quiz = quizStateData?.quiz

  const handleSubmitAnswers = async () => {
    if (!quizState) return
    setIsSubmitting(true)
    try {
      await markRoundAnswers({ quizStateId: quizState._id })
      // Scroll to top after submitting
      window.scrollTo({ top: 0, behavior: "smooth" })
    } catch (err) {
      console.error(
        "Failed to mark answers:",
        err instanceof Error ? err.message : "Unknown error"
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmitForReview = async () => {
    if (!quizState) return
    setIsSubmitting(true)
    try {
      await submitQuizForReview({ quizStateId: quizState._id })
    } catch (err) {
      console.error(
        "Failed to submit quiz:",
        err instanceof Error ? err.message : "Unknown error"
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  // Timer logic + auto-submit when time expires
  useEffect(() => {
    if (!quizState?.roundStartedAt || quizState.status !== "in-progress") {
      setTimeRemaining(null)
      return
    }

    const updateTimer = () => {
      const startTime = new Date(quizState.roundStartedAt!).getTime()
      const duration = quizState.roundDuration
      const now = Date.now()
      const elapsed = now - startTime
      const remaining = Math.max(0, duration - elapsed)

      setTimeRemaining(Math.ceil(remaining / 1000))

      if (remaining <= 0 && !isAutoSubmitting) {
        setIsAutoSubmitting(true)
        void expireRoundTimer({ quizStateId: quizState._id })
          .then(() => markRoundAnswers({ quizStateId: quizState._id }))
          .then(() => submitQuizForReview({ quizStateId: quizState._id }))
          .then(() => {
            void navigate({
              to: "/app/quizzes/$quizStateId",
              params: { quizStateId },
            })
          })
          .catch((err: unknown) => {
            console.error(
              "Failed to auto-submit quiz:",
              err instanceof Error ? err.message : "Unknown error"
            )
          })
      }
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)
    return () => {
      clearInterval(interval)
    }
  }, [quizState, isAutoSubmitting, expireRoundTimer, markRoundAnswers, submitQuizForReview, navigate, quizStateId])

  const handleReturnToQuiz = () => {
    void navigate({
      to: "/app/quizzes/$quizStateId",
      params: { quizStateId: quizStateId },
    })
  }

  if (!quizState || !quiz || !questions) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )
  }

  const roundComplete = isRoundComplete ?? false
  const isInProgress = quizState.status === "in-progress"

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${String(mins)}:${String(secs).padStart(2, "0")}`
  }

  const roundDurationSeconds = quizState.roundDuration / 1000
  const progress =
    timeRemaining !== null && roundDurationSeconds > 0
      ? ((roundDurationSeconds - timeRemaining) / roundDurationSeconds) * 100
      : 0

  // Time info display
  const startDate = parseISO(quiz.startAt)
  const endDate = quiz.endAt ? parseISO(quiz.endAt) : null
  const now = new Date()

  const isExpired = endDate ? now > endDate : false
  const isActive = !isExpired && now >= startDate

  let timeInfo: string
  if (isExpired && endDate) {
    timeInfo = `Ended ${formatDistanceToNow(endDate, { addSuffix: true })}`
  } else if (isActive && endDate) {
    timeInfo = `Ends ${formatDistanceToNow(endDate, { addSuffix: true })}`
  } else if (!isActive) {
    timeInfo = `Starts ${formatDistanceToNow(startDate, { addSuffix: true })}`
  } else {
    timeInfo = "Available now"
  }

  return (
    <div className="container mx-auto max-w-4xl space-y-6 p-6">
      {/* Reminder banner when round is complete but not yet submitted */}
      {roundComplete && quizState.status === "pending" && (
        <div className="rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 p-4">
          <p className="text-amber-800 dark:text-amber-200 font-medium">
            Your answers have been graded. If you challenged any answers, don't
            forget to click "Submit for Review" at the bottom of the page.
          </p>
        </div>
      )}

      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Badge variant="secondary" className="capitalize">
                {quiz.type}
              </Badge>
              {quiz.subject && (
                <Badge variant="outline" className="capitalize">
                  {quiz.subject}
                </Badge>
              )}
              {roundComplete && <Badge variant="default">Complete</Badge>}
            </div>
            <h1 className="text-3xl font-bold">{quiz.name}</h1>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                <span>
                  {formatInLocalTimezone(quiz.startAt, "MMM d")}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="h-4 w-4" />
                <span>{timeInfo}</span>
              </div>
            </div>
          </div>
          {isInProgress && timeRemaining !== null && (
            <div className="text-right">
              <div className="text-3xl font-mono font-bold">
                {formatTime(timeRemaining)}
              </div>
              <div className="text-sm text-muted-foreground">remaining</div>
            </div>
          )}
        </div>
        {isInProgress && timeRemaining !== null && (
          <Progress value={progress} className="h-2" />
        )}
      </div>

      {/* Questions */}
      <div className="space-y-4">
        {questions.map((question, index) => (
          <div key={question._id} className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground">
              Question {index + 1} of {questions.length}
            </div>
            <QuestionAnswer question={question} quizState={quizState} />
          </div>
        ))}
      </div>

      {/* Action buttons */}
      <div className="flex gap-4 justify-between border-t pt-6">
        <Button variant="outline" onClick={handleReturnToQuiz}>
          Back to Quiz
        </Button>
        <div className="flex gap-4">
          {isInProgress && !roundComplete && (
            <Button
              onClick={() => {
                setShowConfirmDialog(true)
              }}
              disabled={isSubmitting}
              size="lg"
            >
              {isSubmitting ? "Submitting..." : "Submit Answers"}
            </Button>
          )}
          {roundComplete && quizState.status === "pending" && (
            <Button
              onClick={() => void handleSubmitForReview()}
              disabled={isSubmitting}
              size="lg"
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              {isSubmitting ? "Submitting..." : "Submit for Review"}
            </Button>
          )}
        </div>
      </div>

      {/* Confirmation Dialog for Submit Answers */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Answers</DialogTitle>
            <DialogDescription>
              Are you sure you want to submit your answers? This will grade your
              responses and you won't be able to change them.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowConfirmDialog(false)
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShowConfirmDialog(false)
                void handleSubmitAnswers()
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Yes, Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
